import axios from "axios";

import store from "../store";
import { showHideLoader } from "../store/Loader/actions";
import { generateUploadLink } from "../network/apis/uploads";

// add your S3 bucket directory name here
export const directories = {
  events: "event_data",
  employees: "employees_data",
  companyRegistration: "company_registration",
  applicants: "applicants_data",
  payroll: "payroll_data",
};

/**
 * The backend cleans the name and prefixes it with a random segment, so the name
 * only has to carry the extension — uniqueness is not our problem any more.
 */
function getFileNameWithExtension(file, fileName) {
  if (fileName && fileName.includes(".")) {
    return fileName;
  }
  if (!fileName) {
    return file.name;
  }
  const ext = file.type.split("/")[1] || "bin";
  return `${fileName}.${ext}`;
}

/**
 * The bytes go straight to storage, not to our API: the signature lives in the
 * URL, so this request must carry no `Authorization` and no `x-amz-acl` header
 * (either one invalidates it). Hence bare `axios`, not a portal instance.
 */
async function putFile(uploadLink, file, onProgress) {
  await axios.put(uploadLink, file, {
    headers: { "Content-Type": file.type },
    onUploadProgress: (evt) => {
      if (!onProgress) return;
      const total = evt.total;
      if (total && total > 0) {
        onProgress(Math.min(100, Math.round((evt.loaded * 100) / total)));
      }
    },
  });
}

/**
 * Upload a file through a backend-issued presigned URL and resolve to the
 * permanent link to store: presign -> PUT -> `{ link }`.
 *
 * The link is only returned once the PUT succeeds — a stored link to a file that
 * never landed is worse than a failed upload.
 */
export const uploadToS3 = async (file, fileName, directoryName, options = {}) => {
  const { onProgress, skipGlobalLoader: skipGlobalLoaderOpt } = options;
  const skipGlobalLoader =
    skipGlobalLoaderOpt !== undefined ? skipGlobalLoaderOpt : Boolean(onProgress);

  if (!file) {
    return undefined;
  }

  if (!skipGlobalLoader) {
    store.dispatch(showHideLoader(true));
  }

  try {
    const { uploadLink, downloadLink } = await generateUploadLink({
      fileName: getFileNameWithExtension(file, fileName),
      folderName: directoryName,
    });
    await putFile(uploadLink, file, onProgress);
    return { link: downloadLink };
  } catch (err) {
    console.error(err);
    // Callers that track progress own their error UI and expect a rejection;
    // the rest read `data?.link` off the result without a catch, so they keep
    // the pre-existing "resolve to undefined" behaviour.
    if (onProgress) {
      throw err;
    }
    return undefined;
  } finally {
    if (!skipGlobalLoader) {
      store.dispatch(showHideLoader(false));
    }
  }
};
